"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useSearchParams } from "next/navigation";
import { MCPTokensModal } from "./MCPTokensModal";

type Msg = { role: "user" | "ai"; text: string };
type AttachedFile = { id: string; name: string; size: string };

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

const DEFAULT_COL_A = 360;
const DEFAULT_COL_C = 340;
const MIN_COL_W = 260;
const MAX_COL_W = 600;

/** Strategy Builder (AI mode) — Cockpit 3-column layout with draggable column resizers. */
export function BuilderAIContent() {
  const [colAW, setColAW] = useState(DEFAULT_COL_A);
  const [colCW, setColCW] = useState(DEFAULT_COL_C);
  const [activeGrip, setActiveGrip] = useState<"colA" | "colC" | null>(null);

  const [msgs, setMsgs] = useState<Msg[]>([
    {
      role: "ai",
      text: "Namaste! Main aapki strategy banane me madad karunga. Natural language me batao — kya trade karna hai, kaunsa exchange, entry/exit rules kya hon?",
    },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState<AttachedFile[]>([]);
  const [isListening, setIsListening] = useState(false);
  const [isMcpModalOpen, setIsMcpModalOpen] = useState(false);

  const endRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<any>(null);
  const baseInputRef = useRef<string>("");
  const inputRef = useRef(input);

  useEffect(() => {
    inputRef.current = input;
  }, [input]);

  // Stop speech recognition on unmount
  useEffect(() => {
    return () => {
      try {
        recognitionRef.current?.stop();
      } catch {}
    };
  }, []);

  // Template deep-link (?template=<name>): prefill a fill-in prompt and
  // auto-send once — skeleton values are 0/empty by rule, Builder fills.
  const searchParams = useSearchParams();
  const templateFired = useRef(false);
  useEffect(() => {
    if (templateFired.current) return;
    const tpl = (searchParams?.get("template") || "").trim();
    if (!tpl) return;
    templateFired.current = true;
    const prompt = `Use the "${tpl}" skeleton template. Symbol NIFTY, 1 lot, max loss 5000, max profit 15000. Fill every 0/empty value with sensible tradable numbers and return the complete manifest.`;
    setInput(prompt);
    const t = setTimeout(() => { void sendRef.current?.(prompt); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const toggleVoice = () => {
    if (typeof window === "undefined") return;
    const SpeechRec =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRec) {
      alert("Aapke browser me speech recognition support nahi hai. Chrome ya Edge try kijiye.");
      return;
    }

    if (isListening) {
      try {
        recognitionRef.current?.stop();
      } catch {}
      setIsListening(false);
      return;
    }

    try {
      baseInputRef.current = inputRef.current;
      const recognition = new SpeechRec();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "hi-IN";
      recognition.onstart = () => setIsListening(true);
      recognition.onresult = (event: any) => {
        let fullSessionTranscript = "";
        for (let i = 0; i < event.results.length; i++) {
          fullSessionTranscript += event.results[i][0].transcript;
        }
        const base = baseInputRef.current.trim();
        const speech = fullSessionTranscript.trim();
        if (base && speech) {
          setInput(`${base} ${speech}`);
        } else {
          setInput(base || speech);
        }
      };
      recognition.onerror = () => setIsListening(false);
      recognition.onend = () => setIsListening(false);
      recognition.start();
      recognitionRef.current = recognition;
      setIsListening(true);
    } catch {
      setIsListening(false);
    }
  };

  // Drag tracking refs
  const dragRef = useRef<{ grip: "colA" | "colC"; startX: number; startW: number } | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs]);

  // Load saved column widths from localStorage if any
  useEffect(() => {
    try {
      const sa = localStorage.getItem("bld-ai-col-a");
      if (sa) {
        const wa = parseInt(sa, 10);
        if (wa >= MIN_COL_W && wa <= MAX_COL_W) setColAW(wa);
      }
      const sc = localStorage.getItem("bld-ai-col-c");
      if (sc) {
        const wc = parseInt(sc, 10);
        if (wc >= MIN_COL_W && wc <= MAX_COL_W) setColCW(wc);
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Mouse move handler for column resizing
  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!dragRef.current) return;
    const { grip, startX, startW } = dragRef.current;
    if (grip === "colA") {
      const dx = e.clientX - startX;
      const nw = Math.min(MAX_COL_W, Math.max(MIN_COL_W, startW + dx));
      setColAW(nw);
    } else if (grip === "colC") {
      const dx = startX - e.clientX; // dragging left increases C, dragging right decreases
      const nw = Math.min(MAX_COL_W, Math.max(MIN_COL_W, startW + dx));
      setColCW(nw);
    }
  }, []);

  // Mouse up handler
  const onMouseUp = useCallback(() => {
    if (dragRef.current) {
      try {
        if (dragRef.current.grip === "colA") {
          localStorage.setItem("bld-ai-col-a", String(colAW));
        } else {
          localStorage.setItem("bld-ai-col-c", String(colCW));
        }
      } catch {
        /* ignore */
      }
    }
    dragRef.current = null;
    setActiveGrip(null);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
  }, [colAW, colCW, onMouseMove]);

  const startDrag = (grip: "colA" | "colC", e: React.MouseEvent) => {
    e.preventDefault();
    dragRef.current = {
      grip,
      startX: e.clientX,
      startW: grip === "colA" ? colAW : colCW,
    };
    setActiveGrip(grip);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const resetCol = (grip: "colA" | "colC") => {
    if (grip === "colA") {
      setColAW(DEFAULT_COL_A);
      try {
        localStorage.setItem("bld-ai-col-a", String(DEFAULT_COL_A));
      } catch {}
    } else {
      setColCW(DEFAULT_COL_C);
      try {
        localStorage.setItem("bld-ai-col-c", String(DEFAULT_COL_C));
      } catch {}
    }
  };

  const [strategyManifest, setStrategyManifest] = useState<any>(null);
  const [validationState, setValidationState] = useState<any>(null);

  const send = async (text: string) => {
    const t = text.trim();
    if ((!t && attachments.length === 0) || busy) return;

    let displayMsg = t;
    if (attachments.length > 0) {
      const fileNames = attachments.map((a) => a.name).join(", ");
      displayMsg = t ? `${t}\n📎 [Attached: ${fileNames}]` : `📎 [Attached: ${fileNames}]`;
    }

    setMsgs((m) => [...m, { role: "user", text: displayMsg }]);
    setInput("");
    setAttachments([]);
    setBusy(true);

    if (isListening) {
      try {
        recognitionRef.current?.stop();
      } catch {}
      setIsListening(false);
    }

    try {
      const res = await fetch("/api/ai/strategy/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prompt: displayMsg }),
      });

      const data = await res.json();
      if (data.manifest) {
        setStrategyManifest(data.manifest);
        setValidationState(data.validation || { valid: true });
        setMsgs((m) => [
          ...m,
          {
            role: "ai",
            text: data.explanation || "Lego strategy logic generate ho gayi hai aur Go service se validate ho gayi hai.",
          },
        ]);
      } else {
        setMsgs((m) => [
          ...m,
          {
            role: "ai",
            text: data.error || "Strategy generate karne me dikkat aayi. Please prompt rephrase karein.",
          },
        ]);
      }
    } catch (err: any) {
      setMsgs((m) => [
        ...m,
        {
          role: "ai",
          text: `Generation error: ${err?.message || "Server unreachable"}`,
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const sendRef = useRef<(t: string) => Promise<void>>(null as any);
  useEffect(() => {
    sendRef.current = send;
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", minHeight: 0, flex: 1 }}>
      <div className="ds-sheet ds-bld" data-mode="ai">
        {/* Header — identical to Cockpit & Chart header */}
        <header className="ds-sheet-head">
          <div className="ds-sheet-head-names">
            <input
              placeholder="Name your strategy"
              aria-label="Strategy name"
              value={strategyManifest?.name || ""}
              onChange={(e) => {
                if (strategyManifest) {
                  setStrategyManifest({ ...strategyManifest, name: e.target.value });
                }
              }}
            />
            <input placeholder="Add a short description (optional)" aria-label="Strategy description" defaultValue="" />
          </div>

          <button type="button" className="ds-sheet-issues-btn" aria-haspopup="dialog" aria-expanded="false" title="See what needs fixing">
            {validationState?.valid ? (
              <span className="ds-chip up dot" style={{ color: "#10b981", background: "rgba(16,185,129,0.12)", borderColor: "rgba(16,185,129,0.3)" }}>
                0 to fix · Verified
              </span>
            ) : (
              <span className="ds-chip down dot">
                {validationState?.lint_errors?.length ? `${validationState.lint_errors.length} to fix` : "2 to fix"}
              </span>
            )}
          </button>

          <button type="button" className="ds-sheet-issues-btn" aria-haspopup="dialog" aria-expanded="false" title="Worth knowing — none of these block Deploy">
            <span className="ds-chip">
              <svg viewBox="0 0 24 24" width="11" height="11" role="presentation" style={{ fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round", display: "block" }}>
                <circle cx="12" cy="12" r="9" />
                <path d="M12 11.5v4.5" />
                <path d="M12 8h.01" />
              </svg>
              {validationState?.lint_warnings?.length ? `${validationState.lint_warnings.length} warning` : "1 warning"}
            </span>
          </button>

          <button type="button" className="ds-bld-marginchip" aria-expanded="false">
            <svg viewBox="0 0 24 24" width="11" height="11" role="presentation" style={{ fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round", display: "block" }}>
              <path d="M12 3l9 5-9 5-9-5z" />
              <path d="M3 13l9 5 9-5M3 18l9 5 9-5" />
            </svg>
            Margin
          </button>

          <span className="ds-sr-only" aria-live="polite" aria-atomic="true">
            Exits: Your entry conditions will not close this trade — the only exits are price levels, so the position is held until one is touched, however long that takes. Turn on Exit on reversal, or add an Exit signal.
          </span>

          <div className="ds-bld-modes" role="radiogroup" aria-label="Builder layout">
            <a href="/wealth/trading/builder" role="radio" aria-checked="false" aria-label="Cockpit layout — Everything at once — three zones, nothing hidden." title="Everything at once — three zones, nothing hidden.">Cockpit</a>
            <a href="/wealth/trading/builder/flow" role="radio" aria-checked="false" aria-label="Flow layout — One decision at a time, in the order a trade happens." title="One decision at a time, in the order a trade happens.">Flow</a>
            <a href="/wealth/trading/builder/chart" role="radio" aria-checked="false" aria-label="Chart layout — Draw the strategy on the price chart it will trade." title="Draw the strategy on the price chart it will trade.">Chart</a>
            <a href="/wealth/trading/builder/ai" role="radio" aria-checked="true" aria-label="AI layout — Chat with the copilot to describe your strategy." title="Chat with the copilot to describe your strategy." data-on="true">AI</a>
          </div>

          <div className="ds-sheet-actions">
            <button
              type="button"
              onClick={() => setIsMcpModalOpen(true)}
              className="ds-btn sm"
              style={{
                flexShrink: 0,
                justifyContent: "center",
                gap: 6,
                borderColor: "rgba(99, 102, 241, 0.4)",
                color: "#a5b4fc",
                background: "rgba(99, 102, 241, 0.08)",
              }}
              title="Connect Cursor, Claude Desktop or External AI via MCP (:8217)"
            >
              <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
              MCP Token
            </button>
            <button type="button" className="ds-btn sm" style={{ flexShrink: 0, justifyContent: "center" }}>Save</button>
            <button type="button" className="ds-btn primary sm" style={{ flexShrink: 0, justifyContent: "center" }}>
              Deploy
              <svg viewBox="0 0 24 24" width="12" height="12" role="presentation" style={{ fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round", display: "block" }}>
                <path d="M5 12h14M13 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </header>

        {/* Cockpit Reads spine */}
        <div className="ds-bld-spine" aria-label="Strategy read-back" aria-live="polite">
          <span className="ds-bld-spine-lead">Reads</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flex: "0 0 auto" }}>
            <button type="button" className="ds-bld-seg" title="Jump to this section">AI Copilot mode active</button>
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flex: "0 0 auto" }}>
            <span className="ds-bld-spine-join" aria-hidden="true">·</span>
            <button type="button" className="ds-bld-seg" title="Jump to this section">Describe your idea to build rules</button>
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flex: "0 0 auto" }}>
            <span className="ds-bld-spine-join" aria-hidden="true">·</span>
            <button type="button" className="ds-bld-seg" title="Jump to this section">Columns can be resized</button>
          </span>
        </div>

        {/* 3-Column Cockpit Layout with Draggable Grips */}
        <div
          className="ds-sheet-cols"
          data-resizing={activeGrip ? "true" : "false"}
          style={
            {
              "--sheet-col-a": `${colAW}px`,
              "--sheet-col-c": `${colCW}px`,
              "--sheet-grip-w": "10px",
            } as React.CSSProperties
          }
        >
          {/* ========================================================
              COLUMN 1 (Left): AI Chat
             ======================================================== */}
          <section className="ds-sheet-col ds-sheet-col--instrument ds-bld-zone" aria-label="AI Chat — natural language co-pilot">
            <div className="ds-bld-zh">
              <h3>AI Chat</h3>
              <span className="ds-bld-zhint" title="natural language strategy">natural language strategy</span>
            </div>

            <div className="ds-bld-zscroll">
              {/* Main AI Chat Card — 8px radius */}
              <div
                className="ds-bld-card"
                style={{
                  border: "1px solid var(--ds-line)",
                  borderRadius: 8,
                  background: "var(--ds-surface)",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 440,
                  height: "100%",
                  flex: "1 1 auto",
                }}
              >
                {/* Messages list */}
                <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, padding: "14px 14px 12px" }}>
                  <div style={{ flex: "1 1 auto", overflowY: "auto", display: "flex", flexDirection: "column", gap: 10, minHeight: 0, paddingRight: 2 }}>
                    {msgs.map((m, i) => (
                      <div
                        key={i}
                        style={{
                          alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                          maxWidth: "88%",
                          padding: "9px 12px",
                          borderRadius: 8,
                          background: m.role === "user" ? "var(--ds-primary)" : "var(--ds-surface-2)",
                          color: m.role === "user" ? "#fff" : "var(--ds-text)",
                          fontSize: 13,
                          lineHeight: 1.55,
                          border: m.role === "ai" ? "1px solid var(--ds-line)" : "none",
                        }}
                      >
                        {m.text}
                      </div>
                    ))}
                    {busy && (
                      <div
                        style={{
                          alignSelf: "flex-start",
                          padding: "9px 12px",
                          borderRadius: 8,
                          background: "var(--ds-surface-2)",
                          border: "1px solid var(--ds-line)",
                          fontSize: 13,
                          color: "var(--ds-text-3)",
                        }}
                      >
                        <span className="ds-ai-typing">
                          <span>.</span><span>.</span><span>.</span>
                        </span>
                      </div>
                    )}
                    <div ref={endRef} />
                  </div>

                  {/* Input row — 3 lines vertical text box with attachment and voice controls */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      border: "1px solid var(--ds-line)",
                      borderRadius: 8,
                      background: "var(--ds-surface)",
                      padding: "8px 10px 8px 12px",
                      marginTop: 10,
                      flexShrink: 0,
                    }}
                  >
                    {/* Attached files chips tray */}
                    {attachments.length > 0 && (
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
                        {attachments.map((f) => (
                          <div
                            key={f.id}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              padding: "2px 7px",
                              borderRadius: 6,
                              background: "var(--ds-surface-2)",
                              border: "1px solid var(--ds-line)",
                              fontSize: 11.5,
                              color: "var(--ds-text)",
                            }}
                          >
                            <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--ds-primary)" }}>
                              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                            </svg>
                            <span style={{ maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                            <span style={{ fontSize: 10, color: "var(--ds-text-3)" }}>({f.size})</span>
                            <button
                              type="button"
                              onClick={() => setAttachments((prev) => prev.filter((x) => x.id !== f.id))}
                              style={{ border: "none", background: "transparent", color: "var(--ds-text-3)", cursor: "pointer", padding: "0 2px", fontSize: 13, lineHeight: 1 }}
                              aria-label="Remove attachment"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Hidden file input */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      multiple
                      accept=".py,.json,.csv,.txt,.pdf,.png,.jpg,.jpeg"
                      onChange={(e) => {
                        const files = e.target.files;
                        if (!files || files.length === 0) return;
                        const newItems = Array.from(files).map((f) => ({
                          id: Math.random().toString(36).slice(2, 9),
                          name: f.name,
                          size: formatFileSize(f.size),
                        }));
                        setAttachments((prev) => [...prev, ...newItems]);
                        e.target.value = "";
                      }}
                    />

                    {/* Multiline textarea — 3 vertical lines */}
                    <textarea
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          send(input);
                        }
                      }}
                      placeholder={isListening ? "🎙️ Sun rahe hain, boliye..." : "Strategy describe karo..."}
                      rows={3}
                      style={{
                        width: "100%",
                        border: "none",
                        outline: "none",
                        resize: "none",
                        background: "transparent",
                        color: "var(--ds-text)",
                        fontSize: 13,
                        lineHeight: "1.45",
                        fontFamily: "inherit",
                        minHeight: 58,
                        maxHeight: 130,
                        padding: 0,
                      }}
                    />

                    {/* Action buttons bar: Attachment & Voice on left, Send on right */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        {/* Attachment button */}
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          aria-label="Attach file"
                          title="Attach file / script / document"
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            border: "none",
                            background: "transparent",
                            color: "var(--ds-text-3)",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "background 0.15s, color 0.15s",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "var(--ds-surface-2)";
                            e.currentTarget.style.color = "var(--ds-text)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                            e.currentTarget.style.color = "var(--ds-text-3)";
                          }}
                        >
                          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                          </svg>
                        </button>

                        {/* Voice / Mic button */}
                        <button
                          type="button"
                          onClick={toggleVoice}
                          aria-label="Voice input"
                          title={isListening ? "Listening... Click to stop" : "Voice input (Speak prompt)"}
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: 6,
                            border: "none",
                            background: isListening ? "rgba(239, 68, 68, 0.15)" : "transparent",
                            color: isListening ? "#ef4444" : "var(--ds-text-3)",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "background 0.15s, color 0.15s",
                            animation: isListening ? "ds-voice-pulse 1.2s infinite" : "none",
                          }}
                          onMouseEnter={(e) => {
                            if (!isListening) {
                              e.currentTarget.style.background = "var(--ds-surface-2)";
                              e.currentTarget.style.color = "var(--ds-text)";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isListening) {
                              e.currentTarget.style.background = "transparent";
                              e.currentTarget.style.color = "var(--ds-text-3)";
                            }
                          }}
                        >
                          <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                            <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                            <line x1="12" y1="19" x2="12" y2="23" />
                            <line x1="8" y1="23" x2="16" y2="23" />
                          </svg>
                        </button>
                      </div>

                      {/* Send button */}
                      <button
                        type="button"
                        onClick={() => send(input)}
                        disabled={(!input.trim() && attachments.length === 0) || busy}
                        aria-label="Send"
                        title="Send message"
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: 8,
                          border: "none",
                          background: (input.trim() || attachments.length > 0) ? "var(--ds-primary)" : "var(--ds-surface-2)",
                          color: (input.trim() || attachments.length > 0) ? "#fff" : "var(--ds-text-3)",
                          cursor: (input.trim() || attachments.length > 0) ? "pointer" : "default",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          transition: "background 0.15s, color 0.15s",
                        }}
                      >
                        <svg viewBox="0 0 24 24" width="14" height="14" style={{ fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }}>
                          <path d="M12 19V5M5 12l7-7 7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* ========================================================
              DRAGGABLE GRIP 1 (between Column 1 & Column 2)
             ======================================================== */}
          <div
            className="ds-sheet-grip"
            data-edge="colA"
            data-dragging={activeGrip === "colA" ? "true" : "false"}
            role="separator"
            aria-orientation="vertical"
            aria-label="AI Chat column width"
            title="Drag to resize column · Double-click to reset"
            onMouseDown={(e) => startDrag("colA", e)}
            onDoubleClick={() => resetCol("colA")}
          />

          {/* ========================================================
              COLUMN 2 (Middle): Clean Plain Zone / Dynamic Logic Canvas
             ======================================================== */}
          <section className="ds-sheet-col ds-sheet-col--entry ds-bld-zone" aria-label="Signal — entry and exit conditions">
            <div className="ds-bld-zh">
              <h3>Signal &amp; Rules</h3>
              <span className="ds-bld-zhint" title="strategy logic canvas">strategy logic canvas</span>
            </div>

            <div className="ds-bld-zscroll">
              {!strategyManifest ? (
                <div
                  className="ds-bld-card"
                  style={{
                    padding: "48px 24px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    border: "1px dashed var(--ds-line-2)",
                    borderRadius: 8,
                    background: "var(--ds-surface)",
                    minHeight: 320,
                    flex: "1 1 auto",
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 8,
                      background: "var(--ds-surface-2)",
                      border: "1px solid var(--ds-line)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 14,
                      color: "var(--ds-text-3)",
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
                    </svg>
                  </div>
                  <b style={{ fontSize: 14, fontWeight: 700, color: "var(--ds-text)", marginBottom: 6 }}>Logic Canvas</b>
                  <span style={{ fontSize: 12.5, color: "var(--ds-text-3)", maxWidth: 300, lineHeight: 1.5 }}>
                    Left panel me AI chat se strategy define karein — yahan rules aur conditions generate hokar dikhenge.
                  </span>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {/* Strategy Header Info Card */}
                  <div style={{ border: "1px solid var(--ds-line)", borderRadius: 8, background: "var(--ds-surface)", padding: "12px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--ds-text)" }}>{strategyManifest.name || "AI Strategy"}</span>
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: validationState?.valid ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)", color: validationState?.valid ? "#10b981" : "#ef4444", fontWeight: 600 }}>
                        {validationState?.valid ? "✓ Validated (Port 8195)" : "⚠ Needs Attention"}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", fontSize: 11.5, color: "var(--ds-text-2)" }}>
                      <span style={{ padding: "2px 7px", borderRadius: 6, background: "var(--ds-surface-2)", border: "1px solid var(--ds-line)" }}>
                        {strategyManifest.symbol} ({strategyManifest.exchange})
                      </span>
                      <span style={{ padding: "2px 7px", borderRadius: 6, background: "var(--ds-surface-2)", border: "1px solid var(--ds-line)" }}>
                        TF: {strategyManifest.tf || "5m"}
                      </span>
                      <span style={{ padding: "2px 7px", borderRadius: 6, background: "var(--ds-surface-2)", border: "1px solid var(--ds-line)" }}>
                        Side: {strategyManifest.execution?.side} ({strategyManifest.execution?.product || "MIS"})
                      </span>
                    </div>
                  </div>

                  {/* Indicators Block */}
                  {strategyManifest.indicators?.length > 0 && (
                    <div style={{ border: "1px solid var(--ds-line)", borderRadius: 8, background: "var(--ds-surface)", padding: "12px 14px" }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--ds-text-3)", textTransform: "uppercase", marginBottom: 6 }}>
                        Indicators (TA Nodes)
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {strategyManifest.indicators.map((ind: any, idx: number) => (
                          <div key={idx} style={{ padding: "3px 8px", borderRadius: 6, background: "var(--ds-surface-2)", border: "1px solid var(--ds-line)", fontSize: 12, color: "var(--ds-text)" }}>
                            <b style={{ color: "var(--ds-primary)" }}>{String(ind.kind).toUpperCase()}</b> {ind.period ? `(P: ${ind.period})` : ""} {ind.mult ? `(M: ${ind.mult})` : ""}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Signal Rule AST Block */}
                  <div style={{ border: "1px solid var(--ds-line)", borderRadius: 8, background: "var(--ds-surface)", padding: "12px 14px" }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--ds-text-3)", textTransform: "uppercase", marginBottom: 6 }}>
                      Alpha Signal Condition (AST)
                    </div>
                    <pre style={{ margin: 0, padding: 8, borderRadius: 6, background: "var(--ds-surface-2)", fontSize: 11, fontFamily: "monospace", overflowX: "auto", color: "var(--ds-text)" }}>
                      {JSON.stringify(strategyManifest.alpha, null, 2)}
                    </pre>
                  </div>

                  {/* Multi-Leg Options Spreads (if any) */}
                  {strategyManifest.legs?.length > 0 && (
                    <div style={{ border: "1px solid var(--ds-line)", borderRadius: 8, background: "var(--ds-surface)", padding: "12px 14px" }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--ds-text-3)", textTransform: "uppercase", marginBottom: 6 }}>
                        Options Legs ({strategyManifest.legs.length} legs)
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {strategyManifest.legs.map((leg: any, idx: number) => (
                          <div key={idx} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "5px 8px", borderRadius: 6, background: "var(--ds-surface-2)", border: "1px solid var(--ds-line)", fontSize: 11.5 }}>
                            <span><b>Leg {idx + 1}:</b> {leg.underlying} {leg.offset} {leg.opt_type}</span>
                            <span style={{ fontWeight: 700, color: leg.side === "BUY" ? "#10b981" : "#ef4444" }}>{leg.side}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* ========================================================
              DRAGGABLE GRIP 2 (between Column 2 & Column 3)
             ======================================================== */}
          <div
            className="ds-sheet-grip"
            data-edge="colC"
            data-dragging={activeGrip === "colC" ? "true" : "false"}
            role="separator"
            aria-orientation="vertical"
            aria-label="Risk and size column width"
            title="Drag to resize column · Double-click to reset"
            onMouseDown={(e) => startDrag("colC", e)}
            onDoubleClick={() => resetCol("colC")}
          />

          {/* ========================================================
              COLUMN 3 (Right): Dynamic Risk & Size Zone
             ======================================================== */}
          <section className="ds-sheet-col ds-sheet-col--exits ds-bld-zone" aria-label="Risk and size — protection and sizing">
            <div className="ds-bld-zh">
              <h3>Risk &amp; Size</h3>
              <span className="ds-bld-zhint" title="protection & sizing">protection &amp; sizing</span>
            </div>

            <div className="ds-bld-zscroll">
              {!strategyManifest ? (
                <div
                  className="ds-bld-card"
                  style={{
                    padding: "48px 24px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    border: "1px dashed var(--ds-line-2)",
                    borderRadius: 8,
                    background: "var(--ds-surface)",
                    minHeight: 320,
                    flex: "1 1 auto",
                  }}
                >
                  <div
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 8,
                      background: "var(--ds-surface-2)",
                      border: "1px solid var(--ds-line)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 14,
                      color: "var(--ds-text-3)",
                    }}
                  >
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 3l8 3v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6z" />
                    </svg>
                  </div>
                  <b style={{ fontSize: 14, fontWeight: 700, color: "var(--ds-text)", marginBottom: 6 }}>Risk &amp; Limits</b>
                  <span style={{ fontSize: 12.5, color: "var(--ds-text-3)", maxWidth: 300, lineHeight: 1.5 }}>
                    Take Profit, Stop Loss, Trailing aur safety guard-rails yahan display honge.
                  </span>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {/* Gate / RMS Card */}
                  <div style={{ border: "1px solid var(--ds-line)", borderRadius: 8, background: "var(--ds-surface)", padding: "12px 14px" }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--ds-text-3)", textTransform: "uppercase", marginBottom: 6 }}>
                      Mandatory RMS (Gate)
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ds-text)" }}>
                      <div><b>Gate Kind:</b> {strategyManifest.gate?.kind}</div>
                      {strategyManifest.gate?.max_loss && <div><b>Max Loss:</b> ₹{strategyManifest.gate?.max_loss}</div>}
                      {strategyManifest.gate?.max_profit && <div><b>Profit Target:</b> ₹{strategyManifest.gate?.max_profit}</div>}
                      {strategyManifest.gate?.max_dd_pct && <div><b>Max Drawdown:</b> {strategyManifest.gate?.max_dd_pct}%</div>}
                    </div>
                  </div>

                  {/* Sizing Card */}
                  <div style={{ border: "1px solid var(--ds-line)", borderRadius: 8, background: "var(--ds-surface)", padding: "12px 14px" }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--ds-text-3)", textTransform: "uppercase", marginBottom: 6 }}>
                      Position Sizing
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ds-text)" }}>
                      <div><b>Mode:</b> {strategyManifest.sizing?.mode}</div>
                      {strategyManifest.sizing?.fixed_qty && <div><b>Quantity:</b> {strategyManifest.sizing?.fixed_qty} lots/units</div>}
                      {strategyManifest.sizing?.risk_pct && <div><b>Risk:</b> {strategyManifest.sizing?.risk_pct}% per trade</div>}
                    </div>
                  </div>

                  {/* Trailing & Protection Card (if any) */}
                  {strategyManifest.trail && (
                    <div style={{ border: "1px solid var(--ds-line)", borderRadius: 8, background: "var(--ds-surface)", padding: "12px 14px" }}>
                      <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--ds-text-3)", textTransform: "uppercase", marginBottom: 6 }}>
                        Trailing Stop &amp; Ladder
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ds-text)" }}>
                        {strategyManifest.trail.trigger_pct && <div><b>Trail Trigger:</b> {strategyManifest.trail.trigger_pct}%</div>}
                        {strategyManifest.trail.trail_pct && <div><b>Trail Step:</b> {strategyManifest.trail.trail_pct}%</div>}
                        {strategyManifest.trail.stop_loss_pct && <div><b>Hard Stop Loss:</b> {strategyManifest.trail.stop_loss_pct}%</div>}
                      </div>
                    </div>
                  )}

                  {/* Execution Venue Card */}
                  <div style={{ border: "1px solid var(--ds-line)", borderRadius: 8, background: "var(--ds-surface)", padding: "12px 14px" }}>
                    <div style={{ fontSize: 11.5, fontWeight: 700, color: "var(--ds-text-3)", textTransform: "uppercase", marginBottom: 6 }}>
                      Execution Routing
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--ds-text)" }}>
                      <div><b>Venue:</b> {strategyManifest.execution?.venue === "live" ? "Live Broker" : "Paper Trading (Simulated)"}</div>
                      <div><b>Segment:</b> {strategyManifest.execution?.segment}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* MCP Client Token Modal */}
      <MCPTokensModal isOpen={isMcpModalOpen} onClose={() => setIsMcpModalOpen(false)} />

      <style>{`
        .ds-ai-typing span { animation: ds-blink 1.2s infinite; display: inline-block; }
        .ds-ai-typing span:nth-child(2) { animation-delay: .2s; }
        .ds-ai-typing span:nth-child(3) { animation-delay: .4s; }
        @keyframes ds-blink { 0%, 60%, 100% { opacity: .2; } 30% { opacity: 1; } }
        @keyframes ds-voice-pulse {
          0% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.5); }
          70% { box-shadow: 0 0 0 7px rgba(239, 68, 68, 0); }
          100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }
        }
      `}</style>
    </div>
  );
}
